import { FC, useCallback, useEffect, useMemo, useState } from "react";
import type { Order } from "@/lib/wsTypes";
import { useLocalStorageWithSubscription } from "@sundaeswap/react-hooks";
import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  assetIdKey,
  formatAssetLabel,
  formatAssetValue,
  formatMarketPairLabel,
} from "@/lib/marketUtils";
import { useHandleCancelOrderRequest } from "@/hooks/useHandleCancelOrderRequest";
import { useHandleCancelAllOrdersRequest } from "@/hooks/useHandleCancelAllOrdersRequest";
import { toast } from "sonner";
import { TradingPaneState } from "./TradingPaneState";
import { ConnectWalletButton } from "@/components/WalletConnect/ConnectWalletButton";
import { onCancelAllRequest } from "@/lib/cancelAllRequest";

const FRACTIONAL_BITS = 60n;

/** Convert a u128 fixed-point price (60 fractional bits) to a display string. */
function formatPrice(raw: bigint): string {
  const integer = raw >> FRACTIONAL_BITS;
  const fractional = raw & ((1n << FRACTIONAL_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  // Scale to 18 decimal digits, then trim trailing zeros
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRACTIONAL_BITS) / 2n) >> FRACTIONAL_BITS;
  const fracStr = scaled.toString().padStart(18, "0").replace(/0+$/, "");
  return `${integer}.${fracStr}`;
}

function isTerminalOrderStatus(status: string): boolean {
  return status === "Filled" || status === "Cancelled";
}

/** The order's own status, or one derived from how much of it is left. */
function orderStatus(order: Order): string {
  return order.status?.tag ?? (
    order.remaining === 0n ? "Filled"
    : order.remaining < order.originalAmount ? "PartiallyFilled"
    : "New"
  );
}

function isOpenOrderStatus(status: string): boolean {
  return status === "New" || status === "PartiallyFilled";
}

/** Full locale timestamp, used on mobile and as the desktop tooltip. */
function formatCreatedFull(created: bigint): string {
  return created > 0n ? new Date(Number(created)).toLocaleString() : "—";
}

/** Compact timestamp for the desktop table, which has no room for a full
 * locale date-time: time of day for today's orders, month and day for older
 * ones. The cell carries the full value as its tooltip. */
function formatCreatedCompact(created: bigint): string {
  if (created <= 0n) return "—";
  const date = new Date(Number(created));
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday
    ? date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function clampDisplayValue(value: string, maxSignificantDigits = 6): string {
  const normalized = value.replace(/,/g, "");
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) {
    return value;
  }

  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: maxSignificantDigits,
  }).format(numeric);
}

const statusLabels: Record<string, string> = {
  New: "Open",
  PartiallyFilled: "Partial",
  Filled: "Filled",
  Cancelled: "Cancelled",
};

const statusColors: Record<string, string> = {
  New: "text-sr-mint",
  PartiallyFilled: "text-sr-tangerine",
  Filled: "text-sr-lemon-muted",
  Cancelled: "text-sr-neutral-500",
};

const statusBadgeStyles: Record<string, string> = {
  New: "bg-sr-mint/10 text-sr-mint",
  PartiallyFilled: "bg-sr-tangerine/10 text-sr-tangerine",
  Filled: "bg-sr-lemon-muted/10 text-sr-lemon-muted",
  Cancelled: "bg-white/5 text-sr-neutral-400",
};

import type { Market } from "@/lib/wsTypes";

interface ActivityPanelProps {
  selectedMarket: Market | null;
}

export const ActivityPanel: FC<ActivityPanelProps> = ({ selectedMarket }) => {
  const [marketFilter, setMarketFilter] = useState(true); // true = this market only
  // Persisted: a trader who wants only working orders should not have to
  // re-hide fills on every visit.
  const [hideFilled, setHideFilled] = useLocalStorageWithSubscription<boolean>(
    "sugar-rush:hide-filled-orders",
    false,
  );
  const { myAccountId, markets, ledgerView, loadMoreOrders } = useGummiwormContext();
  // Account/owner ids are hex strings on the wire (wsTypes), so the owner
  // filter compares them directly — no u256 conversion.
  const myOwnerHex = myAccountId;
  const myOrders = useMemo(
    () => myOwnerHex != null
      ? (Object.values(ledgerView?.orders ?? {}) as Order[]).filter(o => o.owner === myOwnerHex)
      : [],
    [ledgerView?.orders, myOwnerHex],
  );

  const { mutateAsync: cancelOrder, isPending: isCancelling } = useHandleCancelOrderRequest();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const { mutateAsync: cancelAllOrders, isPending: isCancellingAll } =
    useHandleCancelAllOrdersRequest();
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);

  const myAccount = ledgerView.account;

  // CancelAllOrders is account-wide, so the count ignores the market filter.
  const openOrderCount = useMemo(
    () => myOrders.filter((order) => isOpenOrderStatus(orderStatus(order))).length,
    [myOrders],
  );

  // Drop back out of the confirm state if the user walks away from it.
  useEffect(() => {
    if (!confirmCancelAll) return;
    const timer = setTimeout(() => setConfirmCancelAll(false), 5000);
    return () => clearTimeout(timer);
  }, [confirmCancelAll]);

  useEffect(() => {
    if (openOrderCount === 0) setConfirmCancelAll(false);
  }, [openOrderCount]);

  const handleCancelAll = useCallback(async () => {
    if (!myAccount) return;
    try {
      await cancelAllOrders({});
      toast.success("Cancelled all open orders");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to cancel orders");
    } finally {
      setConfirmCancelAll(false);
    }
  }, [myAccount, cancelAllOrders]);

  // shift+C runs the same two steps as the button. The toast stands in for the
  // button's label change, which the user may not be looking at.
  useEffect(() => onCancelAllRequest(() => {
    if (openOrderCount === 0) {
      toast.info("No open orders to cancel.");
      return;
    }
    if (confirmCancelAll) {
      void handleCancelAll();
    } else {
      setConfirmCancelAll(true);
      toast.info("Press shift + C again to cancel all open orders.");
    }
  }), [confirmCancelAll, handleCancelAll, openOrderCount]);

  const handleCancel = useCallback(async (orderId: bigint) => {
    if (!myAccount) return;
    const idStr = orderId.toString();
    setCancellingId(idStr);
    try {
      await cancelOrder({
        orderId: idStr,
      });
      toast.success("Order cancelled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to cancel order");
    } finally {
      setCancellingId(null);
    }
  }, [myAccount, cancelOrder]);

  // Selected market's pair keys for filtering
  const selectedBaseKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.base) : null;
  const selectedQuoteKey = selectedMarket ? assetIdKey(selectedMarket.tradingPair.quote) : null;

  const isMarketMatch = (order: Order) => {
    if (!selectedBaseKey || !selectedQuoteKey) return true;
    return assetIdKey(order.pair.base) === selectedBaseKey &&
           assetIdKey(order.pair.quote) === selectedQuoteKey;
  };

  const displayOrders = useMemo(() => {
    const byMarket = marketFilter && selectedMarket ? myOrders.filter(isMarketMatch) : myOrders;
    // "Filled" hides the finished ones; cancelled orders are still history a
    // trader may want to see, so only fully-filled ones go.
    return hideFilled ? byMarket.filter((o) => o.status.tag !== "Filled") : byMarket;
  }, [myOrders, marketFilter, hideFilled, selectedBaseKey, selectedQuoteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort by created descending (newest first)
  const sorted = useMemo(
    () => [...displayOrders].sort((a, b) => Number(b.created - a.created)),
    [displayOrders],
  );

  // Pagination
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = useMemo(
    () => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sorted, page],
  );
  // Reset to page 0 when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [marketFilter, hideFilled, selectedMarket?.key]);

  // Local paging walks what is already loaded; reaching the last page is the
  // cue to fetch the next page from the server. Filters run client-side, so a
  // narrow filter can empty the list while history remains -- fetch then too,
  // otherwise "no orders" would be an artefact of how little we had pulled.
  const historyExhausted = ledgerView.orderHistoryDone;
  const historyLoading = ledgerView.orderHistoryLoading;
  useEffect(() => {
    if (historyExhausted || historyLoading) return;
    const onLastPage = page >= totalPages - 1;
    if (onLastPage) loadMoreOrders();
  }, [page, totalPages, historyExhausted, historyLoading, loadMoreOrders]);

  // Look up market for an order's trading pair
  const marketForOrder = (order: Order) =>
    markets.find((m) => {
      const ob = assetIdKey(order.pair.base);
      const oq = assetIdKey(order.pair.quote);
      const mb = assetIdKey(m.tradingPair.base);
      const mq = assetIdKey(m.tradingPair.quote);
      return ob === mb && oq === mq;
    });

  const renderCancelButton = (orderId: bigint, status: string, owner: string) => {
    if (
      (status !== "New" && status !== "PartiallyFilled") ||
      owner !== myOwnerHex
    ) {
      return null;
    }

    return (
      <button
        onClick={() => handleCancel(orderId)}
        disabled={isCancelling && cancellingId === orderId.toString()}
        className="text-xs text-sr-salmon hover:text-sr-salmon/80 transition-colors disabled:opacity-50"
      >
        {isCancelling && cancellingId === orderId.toString() ? "..." : "Cancel"}
      </button>
    );
  };

  const renderOrderValue = (
    value: string,
    status: string,
    className: string,
  ) => {
    const shouldClamp = isTerminalOrderStatus(status);
    const displayValue = shouldClamp ? clampDisplayValue(value) : value;
    const isClamped = displayValue !== value;

    // The tooltip always carries the full value: on top of the clamping above,
    // a cell can also be truncated by the width of its column.
    return (
      <span
        className={`${className} ${isClamped ? "cursor-help" : ""}`}
        title={value}
      >
        {displayValue}
      </span>
    );
  };

  const emptyState = myAccountId == null
    ? {
        title: "Connect a wallet",
        message: "Connect a wallet to review your orders, fills, and cancellations.",
        action: <ConnectWalletButton />,
      }
    : {
        title: marketFilter && selectedMarket ? "No orders in this market" : "No orders yet",
        message: marketFilter && selectedMarket
          ? "Your account does not have any orders for the selected pair yet."
          : "Your order history will appear here after your first trade.",
      };

  return (
    <section className="sr-panel @container flex h-full flex-col overflow-hidden" aria-label="Activity and orders">
      {/* Tabs */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-sr-border-subtle px-3">
        <h2 className="shrink-0 py-3 text-sm font-medium text-sr-lemon-muted">My Orders</h2>
        <div className="flex min-w-0 items-center gap-2">
          {openOrderCount > 0 && (
            <button
              onClick={() => (confirmCancelAll ? handleCancelAll() : setConfirmCancelAll(true))}
              disabled={isCancellingAll}
              title="Cancels every open order on your account, in every market (shift + C)"
              className={`sr-focus-ring shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${
                confirmCancelAll
                  ? "border-sr-salmon bg-sr-salmon/15 text-sr-salmon"
                  : "border-sr-salmon/30 text-sr-salmon hover:bg-sr-salmon/10"
              }`}
            >
              {isCancellingAll
                ? "Cancelling…"
                : confirmCancelAll
                  ? `Confirm cancel ${openOrderCount}`
                  : "Cancel all"}
            </button>
          )}
          {selectedMarket && (
            <div className="flex shrink-0 items-center gap-1 bg-sr-surface rounded-full p-0.5">
              <button
                onClick={() => setMarketFilter(true)}
                className={`sr-focus-ring px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                  marketFilter
                    ? "bg-sr-surface-raised text-white"
                    : "text-sr-neutral-500 hover:text-sr-neutral-400"
                }`}
              >
                This market
              </button>
              <button
                onClick={() => setMarketFilter(false)}
                className={`sr-focus-ring px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                  !marketFilter
                    ? "bg-sr-surface-raised text-white"
                    : "text-sr-neutral-500 hover:text-sr-neutral-400"
                }`}
              >
                All
              </button>
            </div>
          )}
          <button
            onClick={() => setHideFilled(!hideFilled)}
            aria-pressed={hideFilled}
            className={`sr-focus-ring shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
              hideFilled
                ? "border-sr-lemon-muted bg-sr-lemon-muted/15 text-sr-lemon-muted"
                : "border-white/10 text-sr-neutral-500 hover:text-sr-neutral-400"
            }`}
          >
            {hideFilled ? "Filled hidden" : "Hide filled"}
          </button>
        </div>
      </div>

      {/* Mobile list */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 md:hidden">
        {sorted.length === 0 && (
          <TradingPaneState
            compact
            eyebrow="My Orders"
            title={emptyState.title}
            message={emptyState.message}
            action={emptyState.action}
            className="rounded-2xl border border-white/[0.05] bg-black/10"
          />
        )}
        {paginated.map((order) => {
          const market = marketForOrder(order);
          const pairLabel = market
            ? formatMarketPairLabel(market)
            : `${formatAssetLabel(order.pair.base)}/${formatAssetLabel(order.pair.quote)}`;
          const status = orderStatus(order);
          const isBid = order.side.tag === "Bid";
          const amountAsset = isBid ? order.pair.quote : order.pair.base;
          const createdAt = formatCreatedFull(order.created);
          const details = [
            {
              label: "Amount",
              value: formatAssetValue(amountAsset, order.originalAmount),
              tone: "text-white",
            },
            {
              label: "Remaining",
              value: formatAssetValue(amountAsset, order.remaining),
              tone: "text-sr-neutral-300",
            },
            {
              label: "Price",
              value: formatPrice(order.price),
              tone: "text-white",
            },
          ];

          return (
            <div
              key={order.id.toString()}
              className="rounded-lg border border-white/[0.05] bg-black/10 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-base font-medium text-white">{pairLabel}</div>
                  <div
                    className={`mt-1 text-sm font-medium ${
                      isBid ? "text-sr-mint" : "text-sr-salmon"
                    }`}
                  >
                    {isBid ? "Buy" : "Sell"}
                  </div>
                </div>
                <div
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                    statusBadgeStyles[status] ?? "bg-white/5 text-sr-neutral-400"
                  }`}
                >
                  <div className={statusColors[status] ?? "text-sr-neutral-500"}>
                    {statusLabels[status] ?? status}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {details.map((detail) => (
                  <div
                    key={detail.label}
                    className="min-w-0 rounded-2xl bg-sr-surface px-3 py-2.5"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-sr-neutral-500">
                      {detail.label}
                    </div>
                    {/* min-w-0 + truncate: an unbreakable long number would
                        otherwise widen the grid column and scroll the page. */}
                    <div className="mt-1 text-sm tabular-nums">
                      {renderOrderValue(detail.value, status, `block truncate ${detail.tone}`)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="min-w-0 text-xs text-sr-neutral-500">
                  {createdAt}
                </div>
                <div className="shrink-0">
                  {renderCancelButton(order.id, status, order.owner)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop empty state — kept outside the table so the header
          row's min-width can't clip the centered message in a narrow pane */}
      {sorted.length === 0 && (
        <div className="hidden md:block">
          <TradingPaneState
            eyebrow="My Orders"
            title={emptyState.title}
            message={emptyState.message}
            action={emptyState.action}
          />
        </div>
      )}

      {/* Desktop table (hidden entirely when there are no orders); scrolls
          vertically so the bounded orders row never clips its rows.
          overflow-x-hidden + table-fixed keep the pane from scrolling
          sideways: every column is a share of the pane width and clips its
          own content, instead of the widest cell setting the table width. */}
      <div
        className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${sorted.length === 0 ? "hidden" : "hidden md:block"}`}
      >
        {/* table-fixed takes its column widths from this header row. Created
            is dropped when the pane itself is narrow (a container query, not a
            viewport one — the pane is half of a column, so its width does not
            track the breakpoints). */}
        <table className="w-full table-fixed">
          <thead>
            <tr className="text-[9px] text-sr-neutral-500 uppercase tracking-wide">
              <th className="w-[16%] truncate text-left font-medium pl-3 pr-2 py-3">Pair</th>
              <th className="w-[9%] truncate text-left font-medium px-2 py-3">Side</th>
              <th className="w-[14%] truncate text-left font-medium px-2 py-3">Amount</th>
              <th className="w-[14%] truncate text-left font-medium px-2 py-3">Remaining</th>
              <th className="w-[13%] truncate text-left font-medium px-2 py-3">Price</th>
              <th className="w-[11%] truncate text-left font-medium px-2 py-3">Status</th>
              <th className="w-[12%] truncate text-right font-medium px-2 py-3 @max-[26rem]:hidden">
                Created
              </th>
              <th className="w-[11%] text-right font-medium pl-0 pr-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((order) => {
              const market = marketForOrder(order);
              const pairLabel = market
                ? formatMarketPairLabel(market)
                : `${formatAssetLabel(order.pair.base)}/${formatAssetLabel(order.pair.quote)}`;
              const status = orderStatus(order);
              const isBid = order.side.tag === "Bid";
              // Bids lock quote, asks lock base
              const amountAsset = isBid ? order.pair.quote : order.pair.base;

              return (
                <tr
                  key={order.id.toString()}
                  className="border-t border-sr-border-subtle/50 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="pl-3 pr-2 py-3">
                    <span className="block truncate text-sm text-white font-medium" title={pairLabel}>
                      {pairLabel}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <span className={`block truncate text-sm font-medium ${isBid ? "text-sr-mint" : "text-sr-salmon"}`}>
                      {isBid ? "Buy" : "Sell"}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    {renderOrderValue(
                      formatAssetValue(amountAsset, order.originalAmount),
                      status,
                      "block truncate text-sm text-white tabular-nums",
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {renderOrderValue(
                      formatAssetValue(amountAsset, order.remaining),
                      status,
                      "block truncate text-sm text-sr-neutral-300 tabular-nums",
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {renderOrderValue(
                      formatPrice(order.price),
                      status,
                      "block truncate text-sm text-white tabular-nums",
                    )}
                  </td>
                  <td className="px-2 py-3">
                    <span className={`block truncate text-sm font-medium ${statusColors[status] ?? "text-sr-neutral-500"}`}>
                      {statusLabels[status] ?? status}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-right @max-[26rem]:hidden">
                    <span
                      className="block truncate text-sm text-sr-neutral-300 tabular-nums"
                      title={formatCreatedFull(order.created)}
                    >
                      {formatCreatedCompact(order.created)}
                    </span>
                  </td>
                  <td className="pl-0 pr-3 py-3 text-right">
                    {renderCancelButton(order.id, status, order.owner)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-between border-t border-sr-border-subtle/50 px-4 py-2">
          <span className="text-[11px] text-sr-neutral-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
            {historyLoading
              ? " · loading older…"
              : historyExhausted
                ? ""
                : " · more available"}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="sr-focus-ring rounded-md px-2.5 py-1 text-[11px] font-medium text-sr-neutral-400 transition-colors hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="sr-focus-ring rounded-md px-2.5 py-1 text-[11px] font-medium text-sr-neutral-400 transition-colors hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
